import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { bookmarksApi } from '../services/api';

const BookmarksContext = createContext();

export const useBookmarks = () => {
  const context = useContext(BookmarksContext);
  if (!context) {
    throw new Error('useBookmarks must be used within a BookmarksProvider');
  }
  return context;
};

export const BookmarksProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadFolders = useCallback(async () => {
    if (!isAuthenticated || !user?.username) return;
    try {
      const data = await bookmarksApi.getFolders();
      setFolders(data.folders || []);
      setLoaded(true);
    } catch (err) {
      console.error('[BookmarksContext] Failed to load folders:', err.message);
    }
  }, [isAuthenticated, user?.username]);

  useEffect(() => {
    if (isAuthenticated && user?.username) {
      loadFolders();
    } else {
      setFolders([]);
      setLoaded(false);
    }
  }, [isAuthenticated, user?.username, loadFolders]);

  const isBookmarked = useCallback((author, permlink) => {
    for (const folder of folders) {
      if (folder.items && folder.items.some(item => item.author === author && item.permlink === permlink)) {
        return true;
      }
    }
    return false;
  }, [folders]);

  const getBookmarkFolders = useCallback((author, permlink) => {
    return folders.filter(folder =>
      folder.items && folder.items.some(item => item.author === author && item.permlink === permlink)
    ).map(f => f.id);
  }, [folders]);

  const createFolder = useCallback(async (name) => {
    const data = await bookmarksApi.createFolder(name);
    setFolders(prev => [...prev, data.folder]);
    return data.folder;
  }, []);

  const renameFolder = useCallback(async (folderId, newName) => {
    const data = await bookmarksApi.renameFolder(folderId, newName);
    setFolders(prev => prev.map(f =>
      f.id === folderId ? { ...f, name: data.folder.name } : f
    ));
    return data.folder;
  }, []);

  const deleteFolder = useCallback(async (folderId) => {
    await bookmarksApi.deleteFolder(folderId);
    setFolders(prev => prev.filter(f => f.id !== folderId));
  }, []);

  const addBookmark = useCallback(async (folderId, author, permlink) => {
    await bookmarksApi.addBookmark(folderId, author, permlink);
    setFolders(prev => prev.map(f =>
      f.id === folderId && !(f.items || []).some(item => item.author === author && item.permlink === permlink)
        ? { ...f, items: [...(f.items || []), { author, permlink, added_at: new Date().toISOString() }] }
        : f
    ));
  }, []);

  const removeBookmark = useCallback(async (folderId, author, permlink) => {
    await bookmarksApi.removeBookmark(folderId, author, permlink);
    setFolders(prev => prev.map(f =>
      f.id === folderId
        ? { ...f, items: (f.items || []).filter(item => !(item.author === author && item.permlink === permlink)) }
        : f
    ));
  }, []);

  const toggleBookmark = useCallback(async (folderId, author, permlink) => {
    const folder = folders.find(f => f.id === folderId);
    const exists = folder?.items?.some(item => item.author === author && item.permlink === permlink);
    if (exists) {
      await removeBookmark(folderId, author, permlink);
    } else {
      await addBookmark(folderId, author, permlink);
    }
  }, [folders, addBookmark, removeBookmark]);

  return (
    <BookmarksContext.Provider value={{
      folders,
      loaded,
      isBookmarked,
      getBookmarkFolders,
      createFolder,
      renameFolder,
      deleteFolder,
      addBookmark,
      removeBookmark,
      toggleBookmark,
      reload: loadFolders,
    }}>
      {children}
    </BookmarksContext.Provider>
  );
};
